import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { USER_STORAGE_KEY } from '@/util'

import { FilterActions, useFiltersContext } from '..'
import { UserContext_State, UserContext_Type, UserContext_User } from './user.types'

export const initialState: UserContext_State = {
  user: null as UserContext_User | null,
  isLoading: false,
  isLoggedIn: false,
  isAdmin: false,
}

export const UserContextStore = createContext<UserContext_Type | undefined>(undefined)

export function UserContextProvider({ children }: { children: React.ReactNode; value?: typeof initialState }) {
  const [userState, setState] = useState<UserContext_State>(initialState)
  const [, FilterDispatch] = useFiltersContext()

  useEffect(() => {
    // hydrate context state on load from LocalStorage
    const storage = localStorage.getItem(USER_STORAGE_KEY)
    if (storage) {
      const parsed = JSON.parse(storage)
      const user = parsed?.user
      document.cookie = `token=${user?.auth_token || ''};path=/`
      setState({
        ...parsed,
        isLoggedIn: user?.auth_token ? true : false,
      })
      if (user?.marketSegmentIds && user?.marketSegmentIds.length) {
        FilterDispatch(
          FilterActions.setMarketSegments({
            ids: user?.marketSegmentIds,
            fieldOperationsHubs: [],
            fieldOperationsRegions: [],
            marketSegmentTypes: [],
          }),
        )
      }
    } else {
      setState((prevState) => ({ ...prevState, user: null }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Update local storage anytime state changes
    if (userState !== initialState) {
      const userStorage = JSON.stringify(userState)
      localStorage.setItem(USER_STORAGE_KEY, userStorage)
    }
  }, [userState])

  const updateUser = useCallback((user: UserContext_User) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: false,
      isLoggedIn: user?.auth_token ? true : false,
      user,
    }))
  }, [])

  const refreshToken = useCallback((token: string) => {
    setState((prevState) => {
      const prevUser = prevState?.user as UserContext_User
      return {
        ...prevState,
        user: {
          ...prevUser,
          auth_token: token,
        },
      }
    })
  }, [])

  const logoutUser = useCallback(() => {
    setState({ ...initialState, isLoggedIn: false })
  }, [])

  const setLoading = useCallback((val: boolean) => {
    setState((prevState) => {
      return {
        ...prevState,
        isLoading: val,
      }
    })
  }, [])

  return (
    <UserContextStore.Provider value={{ ...userState, updateUser, logoutUser, refreshToken, setLoading }}>
      {children}
    </UserContextStore.Provider>
  )
}

export const useUserContext = () => {
  const context = useContext(UserContextStore)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider')
  }
  return context
}
